import {
	UsersApiResponse,
	TasksApiResponse,
	LogApiResponse,
	Role,
	Form,
	EventsApiResponse,
	Document,
	DocumentsApiResponse,
} from '@repo/shared/domain';
import { guardedClient as clientRequest } from '@repo/shared/helpers';

const groupPrefix = (groupId: string) => `/api/personal-dashboard/groups/${groupId}`;

export const getGroupUsers = async (groupId: string, query?: string): Promise<UsersApiResponse> => {
	const { body } = await clientRequest.get<undefined, UsersApiResponse>(
		`${groupPrefix(groupId)}/users${query ? `?${query}` : ''}`,
		{ schema: undefined },
	);
	return body;
};

export const getGroupTasks = async (groupId: string, query?: string): Promise<TasksApiResponse> => {
	const { body } = await clientRequest.get<undefined, TasksApiResponse>(
		`${groupPrefix(groupId)}/tasks${query ? `?${query}` : ''}`,
		{ schema: undefined },
	);
	return body;
};

export const getGroupRoles = async (groupId: string): Promise<Role[]> => {
	const { body } = await clientRequest.get<undefined, Role[]>(`${groupPrefix(groupId)}/roles`, { schema: undefined });
	return body;
};

export const getGroupDocuments = async (
	groupId: string,
	params?: { filter?: string; page?: number },
): Promise<DocumentsApiResponse> => {
	const query = new URLSearchParams();
	if (params?.page !== undefined) {
		query.set('page', params.page.toString());
	} else {
		query.set('noPagination', 'true');
	}
	if (params?.filter === 'all') query.set('all', 'yes');
	const { body } = await clientRequest.get<undefined, DocumentsApiResponse>(
		`${groupPrefix(groupId)}/documents?${query.toString()}`,
		{ schema: undefined },
	);
	return body;
};

export const getGroupDocument = async (groupId: string, documentId: string): Promise<Document | null> => {
	const q = new URLSearchParams({ fileIds: documentId, includeLogs: 'true', all: 'yes' });
	const { body } = await clientRequest.get<undefined, DocumentsApiResponse>(
		`${groupPrefix(groupId)}/documents?${q.toString()}`,
		{ schema: undefined },
	);
	return body?.data?.[0] ?? null;
};

export const getGroupLogs = async (groupId: string, query: string): Promise<LogApiResponse> => {
	const { body } = await clientRequest.get<undefined, LogApiResponse>(`${groupPrefix(groupId)}/logs?${query}`, {
		schema: undefined,
	});
	return body;
};

export const getGroupEvents = async (groupId: string, query?: string): Promise<EventsApiResponse> => {
	const { body } = await clientRequest.get<undefined, EventsApiResponse>(
		`${groupPrefix(groupId)}/events${query ? `?${query}` : ''}`,
		{ schema: undefined },
	);
	return body;
};

export const getGroupTaskDocuments = async (groupId: string, documentIds: string[]): Promise<Document[]> => {
	if (documentIds.length === 0) return [];
	const query = new URLSearchParams({ noPagination: 'true', fileIds: documentIds.join(',') });
	const { body } = await clientRequest.get<undefined, DocumentsApiResponse>(
		`${groupPrefix(groupId)}/documents?${query.toString()}`,
		{ schema: undefined },
	);
	return (body?.data ?? []).filter((doc) => documentIds.includes(doc._id));
};

export const getGroupForm = async (formId: string, query?: string): Promise<Form | undefined> => {
	try {
		const { body } = await clientRequest.get<undefined, Form>(
			`/api/personal-dashboard/groups/forms/${formId}${query ? `?${query}` : ''}`,
			{ schema: undefined },
		);
		return body;
	} catch {
		return undefined;
	}
};
